var App = App || {};

App.EventDetail = (function ($) {
	"use strict";

	function Product(data) {
		data = data || {};
		this.key = ko.observable(data.key || "");
		this.contentType = ko.observable(data.contentType || "");
		this.price = ko.observable(data.price || 0.0);
		this.currency = ko.observable(data.currency || "USD");
		this.title = ko.observable(data.title || "");

		const props = data.properties || {};
		this.eventStart = ko.observable(props.eventStart ? new Date(props.eventStart) : null);
		this.eventEnd = ko.observable(props.eventEnd ? new Date(props.eventEnd) : null);
		this.registrationOpens = ko.observable(props.registrationOpens ? new Date(props.registrationOpens) : null);
		this.registrationCloses = ko.observable(props.registrationCloses ? new Date(props.registrationCloses) : null);
		this.childProducts = (data.bundleOptions || []).map(p => new Product(p));
		this.seriesPricing = ko.observable(props.seriesPricing || "");

		this.webinars = ko.pureComputed(() => {
			return this.childProducts.filter(p => p.contentType() === "Page_RegistrationEvent");
		});

		this.addOns = ko.pureComputed(() => {
			return this.childProducts.filter(p => p.contentType() !== "Page_RegistrationEvent");
		});

		this.isSeries = ko.pureComputed(() => this.contentType() === "Page_WebinarSeries");

		this.isOpen = ko.pureComputed(() => {
			const now = new Date();
			const opens = this.registrationOpens();
			const closes = this.registrationCloses();

			const hasValidOpens = opens instanceof Date && !isNaN(opens);
			const hasValidCloses = closes instanceof Date && !isNaN(closes);

			if (hasValidOpens && hasValidCloses) {
				return now >= opens && now <= closes;
			}

			if (hasValidOpens && !hasValidCloses) {
				return now >= opens;
			}

			if (!hasValidOpens && hasValidCloses) {
				return now <= closes;
			}

			return true;
		});

		this.seriesDescriptor = ko.pureComputed(() => {
			const start = this.eventStart();
			const end = this.eventEnd();

			if (!(start instanceof Date) || isNaN(start) || !(end instanceof Date) || isNaN(end)) {
				return this.title();
			}

			const weekday = start.toLocaleDateString("en-US", { weekday: "short" });
			const monthDay = start.toLocaleDateString("en-US", { month: "long", day: "numeric" });

			const formatTime = (d) =>
				d.toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true
				}).replace("AM", "AM").replace("PM", "PM");

			const formatTimeWithZone = (d) =>
				d.toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true,
					timeZoneName: "short"
				}).replace("AM", "AM").replace("PM", "PM");

			const startTime = formatTime(start);
			const endTime = formatTimeWithZone(end);

			return `${weekday}, ${monthDay} | ${startTime} to ${endTime}`;
		});

		this.formattedPrice = ko.pureComputed(() => {
			const val = this.price() ?? 0;
			if (val <= 0)
				return 'Free';

			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: this.currency()
			}).format(val ?? 0);
		});

		this.eventDateDisplay = ko.pureComputed(() => {
			const start = this.eventStart();
			const end = this.eventEnd();

			if (!(start instanceof Date) || isNaN(start) || !(end instanceof Date) || isNaN(end)) {
				return "";
			}

			const sameDay = start.toDateString() === end.toDateString();

			const dateFormatter = new Intl.DateTimeFormat('en-US', {
				month: 'long',
				day: 'numeric',
				year: 'numeric'
			});

			const formatTimeParts = (date) => {
				const parts = new Intl.DateTimeFormat('en-US', {
					hour: 'numeric',
					minute: '2-digit',
					hour12: true,
					timeZoneName: 'short'
				}).formatToParts(date);

				const hourMinute = parts
					.filter(p => p.type === "hour" || p.type === "minute" || p.type === "literal")
					.map(p => p.value)
					.join("");

				const meridiem = parts.find(p => p.type === "dayPeriod")?.value.toLowerCase();
				const formattedMeridiem = meridiem === "am" ? "a.m." : "p.m.";

				const tz = parts.find(p => p.type === "timeZoneName")?.value ?? "";

				return { time: hourMinute, meridiem: formattedMeridiem, tz };
			};

			const startParts = formatTimeParts(start);
			const endParts = formatTimeParts(end);

			let timeStr = "";
			if (sameDay) {
				if (startParts.meridiem === endParts.meridiem) {
					timeStr = `${startParts.time} to ${endParts.time} ${endParts.meridiem}`;
				} else {
					timeStr = `${startParts.time} ${startParts.meridiem} to ${endParts.time} ${endParts.meridiem}`;
				}
				timeStr += ` ${endParts.tz}`;
				const dateStr = dateFormatter.format(start);
				return `${dateStr}<br>${timeStr}`;
			} else {
				return dateFormatter.format(start);
			}
		});
	}

	function RegistrationData(data) {
		data = data || {};

		this.product = ko.observable(new Product(data.product));
		this.name = ko.observable(data.name || "");
		this.phone = ko.observable(data.phone || "");
		this.email = ko.observable(data.email || "");
		this.uuid = ko.observable(data.uuid || generateShortId());

		this.selectedWebinars = ko.observableArray([]);
		this.selectedAddOns = ko.observableArray([]);

		this.totalPrice = ko.observable(0);
		this.isCalculating = ko.observable(false);
		this.calcError = ko.observable("");

		this.product.subscribe(newProduct => {
			const openWebinars = newProduct
				.webinars()
				.filter(w => w.isOpen?.())
				.map(w => w.key());
			this.selectedWebinars(openWebinars);
		});

		this.canSubmit = ko.pureComputed(() => {
			return this.product().webinars().length === 0 || this.selectedWebinars().length;
		});

		this.canSubmit.subscribe((value) => {
			if (!value) {
				this.selectedAddOns([]);
			}
		});

		this.allWebinarsSelected = ko.pureComputed({
			read: () => this.selectedWebinars().length === this.product().webinars().length,
			write: (checked) => {
				if (checked) {
					this.selectedWebinars(this.product().webinars().map(w => w.key()));
				} else {
					this.selectedWebinars([]);
				}
			}
		});

		this.formattedTotalPrice = ko.pureComputed(() => {
			return new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: this.product()?.currency?.() || "USD"
			}).format(this.totalPrice() || 0);
		});

		this.selectionSignature = ko.pureComputed(() => {
			const bundleKey = this.product().key();
			const items = [...this.selectedWebinars(), ...this.selectedAddOns()].sort();
			return JSON.stringify({ bundleKey, items });
		});

		this._calcXhr = null;
		this.recalculate = () => {
			const bundleKey = this.product().key();

			if (!this.product().key()) {
				this.totalPrice(0);
				return;
			}

			const items = [...this.selectedWebinars(), ...this.selectedAddOns()].map(key => ({
				product: key,
				productVariant: null,
				quantity: 1
			}));

			if (this._calcXhr && this._calcXhr.readyState !== 4) {
				this._calcXhr.abort();
			}

			const request = {
				bundleProduct: bundleKey,
				items: items
			};

			this.isCalculating(true);
			this.calcError("");

			this._calcXhr = $.ajax({
				url: "/api/bundle/calculate/",
				contentType: "application/json; charset=utf-8",
				method: "POST",
				data: JSON.stringify(request),
				dataType: "json"
			})
				.done((response) => {
					const total = response?.lines[0]?.price?.total ?? response?.total ?? 0;
					this.totalPrice(Number(total) || 0);
				})
				.fail((jqXHR, textStatus) => {
					if (textStatus !== "abort") {
						this.calcError("Pricing service error.");
						this.totalPrice(0);
					}
				})
				.always(() => {
					this.isCalculating(false);
					this._calcXhr = null;
				});
		};

		this.selectionSignature
			.extend({ rateLimit: { timeout: 150, method: "notifyWhenChangesStop" } })
			.subscribe(() => this.recalculate());
	}

	const viewModel = {
		registrationData: ko.observable(new RegistrationData()),

		addToCart: function (data, event) {
			event.preventDefault();
			const $form = $(event.currentTarget).closest("form");
			const form = $form[0];
			const $offcanvas = $(event.currentTarget).closest(".offcanvas");
			const addMore = $(event.currentTarget).data("more");


			$form.find("input").each((_, el) => el.setCustomValidity(""));
			$form.removeClass("was-validated");

			if (form.checkValidity()) {
				var selectedProducts = [...data.selectedWebinars(), ...data.selectedAddOns()].map(key => {
					const selectedProduct = data.product().childProducts.find(p => p.key() === key);
					return {
						product: key,
						productVariant: null,
						quantity: 1,
						userData: {
							cartDescription: selectedProduct?.title?.() || "",
						}
					};
				});

				App.Commerce
					.Bundle(data.product().key(), null, 1, selectedProducts, {
						"cartDescription": `Attending: ${data.name()}`,
						"seat": {
							"name": data.name(),
							"phone": data.phone(),
							"email": data.email()
						},
					})
					.done(() => {
						viewModel.registrationData().name("");
						viewModel.registrationData().phone("");
						viewModel.registrationData().email("");
						viewModel.registrationData().uuid(generateShortId());
						form.classList.remove('was-validated');

						if (!addMore)
							window.location.href = "/shopping-cart";
					});
			}
			applyValidationMessages($form);
			form.classList.add('was-validated');
		},

		calculate: function (data, event) {
			return data.recalculate();
		}
	};

	function generateShortId() {
		return Math.random().toString(36).substring(2, 9);
	}

	function getPurchaseProduct(productKey) {
		return $.ajax({
			url: `/api/products/${productKey}`,
			contentType: 'application/json; charset=utf-8',
			method: 'GET',
			dataType: 'json'
		}).fail(function (jqXHR) {
			App.Base.ShowError("Unable to complete request. Please try again.");
		});
	}

	function init() {
		$(".offcanvas-registration.event-single").each(function () {
			ko.applyBindings(viewModel, this);

			$(this).on("show.bs.offcanvas", function (event) {
				const $trigger = $(event.relatedTarget);

				if (!$trigger || !$trigger.length)
					return;

				getPurchaseProduct($trigger.data("product-key") || "")
					.done(function (response) {
						if (!response) {
							App.Base.ShowError("API did not respond.");
							return;
						};

						if (response.success && response.data) {
							let purchaseProduct = response.data;
							const registrationData = viewModel.registrationData();
							registrationData.product(new Product(purchaseProduct));
							registrationData.name("");
							registrationData.phone("");
							registrationData.email("");
							registrationData.selectedAddOns([]);
							registrationData.uuid(generateShortId());
							return;
						}
					});
			});
		});
	}

	function applyValidationMessages($form) {
		$form.find(":invalid").each(function () {
			let msg = "";

			switch (true) {
				case this.validity.valueMissing:
					msg = $(this).data("val-required") || "A value is required.";
					break;
				case this.validity.typeMismatch:
					msg = $(this).data("val-type") || "Incorrect format.";
					break;
				default:
					msg = this.validationMessage || "";
					break;
			}

			$(this).siblings(".field-validation-error").text(msg);
		});
	}

	return {
		init: init
	};

})(jQuery);

$(function () {
	App.EventDetail.init();
});
